import React, { useState } from 'react';
import { Form, Input, Button, message, Select } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ContractDataRow } from './ContractDataRow';
import { WeightDataRow } from './WeightDataRow';
import { SectionHeader, SubsectionHeader } from './SectionHeader';
import { SectionDivider } from './SectionDivider';
import { NotesSection } from './NotesSection';
import { CardContainer } from './CardContainer';
import { GridLayout } from './GridLayout';
import { ShipsWithList } from './ShipsWithList';
import { ShipmentMultiSelect } from './ShipmentMultiSelect';
import { SharedContainerIndicator } from './SharedContainerIndicator';
import { spacing } from '../design-system/tokens';
import { Shipment } from './types';
import ContainerCard from './ContainerCard';
import GoodsCard from './GoodsCard';

const { TextArea } = Input;
const { Option } = Select;

// Ship icon SVG (matching Figma design)
const ShipIcon: React.FC = () => (
  <svg 
    width="36" 
    height="32" 
    viewBox="0 0 36 32" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M4 24H32L28 12H8L4 24Z" 
      stroke="currentColor" 
      strokeWidth="2" 
      fill="none"
    />
    <path 
      d="M2 28H34" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    <path 
      d="M12 12V8C12 6.89543 12.8954 6 14 6H22C23.1046 6 24 6.89543 24 8V12" 
      stroke="currentColor" 
      strokeWidth="2"
    />
  </svg>
);

interface ShipmentDetailsCardProps {
  shipment: Shipment;
  isEditing: boolean;
  onEditToggle: () => void;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
  className?: string;
}

const ShipmentDetailsCard: React.FC<ShipmentDetailsCardProps> = ({ 
  shipment, 
  isEditing,
  onEditToggle,
  onSave,
  onCancel,
  className = '' 
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Fetch booking by options for dropdown
  const { data: bookingByData } = useQuery({
    queryKey: ['booking-by'],
    queryFn: async () => {
      return await api.get('/booking-by', { params: { active: true, limit: 100 } }) as any;
    },
    enabled: isEditing,
  });

  // Fetch forwarders for dropdown
  const { data: forwardersData } = useQuery({
    queryKey: ['forwarders'],
    queryFn: async () => {
      return await api.get('/forwarders', { params: { active: true, limit: 100 } }) as any;
    },
    enabled: isEditing,
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      // Transform form values to API format
      const apiData: any = {};
      
      if (values.start !== undefined) apiData.start = values.start;
      if (values.end !== undefined) apiData.end = values.end;
      if (values.shipsWith !== undefined) apiData.ships_with = values.shipsWith;
      if (values.packingNotes !== undefined) apiData.packing_notes = values.packingNotes;
      if (values.noteOne !== undefined) apiData.shipment_note_one = values.noteOne;
      if (values.noteTwo !== undefined) apiData.shipment_note_two = values.noteTwo;
      if (values.bookingBy !== undefined) apiData.booking_by_id = values.bookingBy;
      if (values.fclTruck !== undefined) apiData.fcl_truck = values.fclTruck;
      if (values.siAttnTo !== undefined) apiData.si_attn_to = values.siAttnTo;
      if (values.forwarder !== undefined) apiData.forwarder_id = values.forwarder;
      if (values.trucking !== undefined) apiData.trucking = values.trucking;
      
      await onSave(apiData);
      message.success('Shipment updated successfully');
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please fix validation errors');
      } else {
        message.error(error.message || 'Failed to update shipment');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Initialize form with shipment data
  React.useEffect(() => {
    if (isEditing) {
      // Parse the combined "start / end" string back into separate fields
      const shipmentParts = shipment.timing?.shipment?.split(' / ') || [];
      const start = shipmentParts[0] || '';
      const end = shipmentParts[1] || '';
      
      form.setFieldsValue({
        start: start,
        end: end,
        shipsWith: shipment.timing?.shipsWith || '',
        packingNotes: shipment.timing?.packingNotes || '',
        noteOne: shipment.timing?.note1 || '',
        noteTwo: shipment.timing?.note2 || '',
        bookingBy: shipment.bookingInfo?.bookingById || '',
        fclTruck: shipment.bookingInfo?.fclTruck || '',
        siAttnTo: shipment.bookingInfo?.siAttnTo || '',
        forwarder: shipment.bookingInfo?.forwarderId || '',
        trucking: shipment.bookingInfo?.trucking || '',
      });
    }
  }, [isEditing, shipment, form]);

  // Add null checks for shipment data
  if (!shipment) {
    return (
      <CardContainer variant="secondary">
        <SectionHeader
          title="Shipment"
          icon={<div style={{ width: '36px', height: '32px' }}><ShipIcon /></div>}
          showBottomBorder={true}
        />
        <div style={{ padding: spacing.xl, textAlign: 'center', color: '#666' }}>
          No shipment data available
        </div>
      </CardContainer>
    );
  }

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      
      {/* Main shipment card */}
      <CardContainer variant="secondary">
        {/* Shipment Header */}
        <SectionHeader
          title={`Shipment ${shipment.shipmentNumber || 'Unknown'}`}
          icon={<div style={{ width: '36px', height: '32px' }}><ShipIcon /></div>}
          showBottomBorder={true}
          actions={
            isEditing ? (
              <>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  onClick={handleSave}
                  loading={saving}
                >
                  Save
                </Button>
                <Button 
                  icon={<CloseOutlined />} 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button 
                icon={<EditOutlined />} 
                onClick={onEditToggle}
              >
                Edit
              </Button>
            )
          }
        />

        {/* Timing & Notes Section */}
        <div>
          <SubsectionHeader title="Timing & Notes" />

          {isEditing ? (
            <Form form={form} layout="vertical">
              <GridLayout columns="120px 120px 150px 1fr" gap="2xl" style={{ marginBottom: spacing.xl }}>
                <Form.Item label="Start" name="start">
                  <Input placeholder="e.g. FH May 25" />
                </Form.Item>
                <Form.Item label="End" name="end">
                  <Input placeholder="e.g. LH May 25" />
                </Form.Item>
                <Form.Item
                  label="Ships With"
                  name="shipsWith"
                  getValueProps={(value) => ({
                    value: value ? value.split(',').filter(Boolean) : [],
                  })}
                  normalize={(value) => (Array.isArray(value) ? value.join(',') : value)}
                >
                  <ShipmentMultiSelect currentShipmentId={shipment.id} />
                </Form.Item>
                <Form.Item label="Packing Notes" name="packingNotes">
                  <Input placeholder="Enter packing notes" />
                </Form.Item>
              </GridLayout>
              <GridLayout columns="1fr">
                <Form.Item label="Note 1" name="noteOne">
                  <TextArea rows={2} placeholder="Enter note 1" />
                </Form.Item>
                <Form.Item label="Note 2" name="noteTwo">
                  <TextArea rows={2} placeholder="Enter note 2" />
                </Form.Item>
              </GridLayout>
            </Form>
          ) : (
            <>
              <GridLayout
                columns="120px 150px 1fr"
                gap="2xl"
                style={{ marginBottom: spacing.xl, alignItems: 'start' }}
              >
                <ContractDataRow label="Shipment" value={shipment.timing?.shipment || 'N/A'} />
                <div>
                  <ContractDataRow
                    label="Ships With"
                    value={<ShipsWithList shipsWith={shipment.timing?.shipsWith} />}
                  />
                </div>
                <ContractDataRow label="Packing Notes" value={shipment.timing?.packingNotes || 'N/A'} />
              </GridLayout>

              {(shipment.timing?.note1 || shipment.timing?.note2) && (
                <NotesSection
                  notes={[
                    ...(shipment.timing?.note1 ? [{ label: 'Note 1', content: shipment.timing.note1 }] : []),
                    ...(shipment.timing?.note2 ? [{ label: 'Note 2', content: shipment.timing.note2 }] : [])
                  ]}
                />
              )}
            </>
          )}
        </div>

        {/* Booking Information Section */}
        <div>
          <SubsectionHeader title="Booking Information" />
          {isEditing ? (
            <Form form={form} layout="vertical">
              <GridLayout columns="repeat(5, 1fr)">
                <Form.Item label="Booking By" name="bookingBy">
                  <Select
                    placeholder="Select booking by"
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const children = option?.children as string | undefined;
                      return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                    }}
                  >
                    {bookingByData?.booking_by?.map((item: any) => (
                      <Option key={item.id} value={item.id}>
                        {item.who}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="FCL Truck" name="fclTruck">
                  <Input placeholder="Enter FCL truck" />
                </Form.Item>
                <Form.Item label="SI Attn To" name="siAttnTo">
                  <Input placeholder="Enter SI attn to" />
                </Form.Item>
                <Form.Item label="Forwarder" name="forwarder">
                  <Select
                    placeholder="Select forwarder"
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const children = option?.children as string | undefined;
                      return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                    }}
                  >
                    {forwardersData?.forwarders?.map((item: any) => (
                      <Option key={item.id} value={item.id}>
                        {item.displayName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Trucking" name="trucking">
                  <Input placeholder="Enter trucking" />
                </Form.Item>
              </GridLayout>
            </Form>
          ) : (
            <GridLayout columns="repeat(5, 1fr)">
              <ContractDataRow label="Booking By" value={shipment.bookingInfo?.bookingBy || 'N/A'} />
              <ContractDataRow label="FCL Truck" value={shipment.bookingInfo?.fclTruck || 'N/A'} />
              <ContractDataRow label="SI Attn To" value={shipment.bookingInfo?.siAttnTo || 'N/A'} />
              <ContractDataRow label="Forwarder" value={shipment.bookingInfo?.forwarder || 'N/A'} />
              <ContractDataRow label="Trucking" value={shipment.bookingInfo?.trucking || 'N/A'} />
            </GridLayout>
          )}
        </div>

        {/* Shipment Notes Section */}
        {(shipment.notes?.sellerNotes || shipment.notes?.buyerNotes) && (
          <div>
            <SubsectionHeader title="Notes" />
            <NotesSection 
              notes={[
                ...(shipment.notes?.sellerNotes ? [{ label: 'Seller Notes', content: shipment.notes.sellerNotes }] : []),
                ...(shipment.notes?.buyerNotes ? [{ label: 'Buyer Notes', content: shipment.notes.buyerNotes }] : [])
              ]}
            />
          </div>
        )}

        {/* Container Details */}
        {Array.isArray(shipment.containers) && shipment.containers.map((container, containerIndex) => (
          <div key={container?.id || Math.random()}>
            <ContainerCard container={container} containerIndex={containerIndex} />

            {/* Goods Details */}
            {Array.isArray(container.goods) && container.goods.map((goods) => (
              <GoodsCard key={goods?.id || Math.random()} goods={goods} />
            ))}
          </div>
        ))}
      </CardContainer>
    </div>
  );
};

export default ShipmentDetailsCard;